import {BookingEngine} from "crqlar-react-common/dist/models/bookingEngine/bookingEngine.model";
import {Alert, Box, Button, Card, CardContent, Stack, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import { useOrganization } from "crqlar-react-common/dist/state/auth.reducer";
import * as React from "react";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import {enqueueSnackbar} from "notistack";

const INTEGRATION_GUIDE_URL = "https://crqlar.notion.site/Integration-der-Booking-Engine-auf-Ihrer-Website-oder-Hotel-App-z-B-Guestnet-25c9e2a69b9980a3b602d60f4ac74cfe";

interface Props {
    bookingEngine?: BookingEngine
}

const BookingEngineInstructions = ({bookingEngine}: Props) => {

    const organization = useOrganization()
    const {t} = useTranslation()

    if (!bookingEngine?.id) return null;

    const scriptEmbed = `<script src="${process.env.REACT_APP_BOOKING_ENGINE_WIDGET_HOST}/booking-widget.js"></script>`
    const divEmbed = `<div class="crqlar-booking-widget" widget_id="${bookingEngine.id}" public_key="${organization?.publicKey}"></div>`

    const firstStepInstruction = t('1. Put this in the page header section (between <head> </head> tags)')
    const secondStepInstruction = t('2. Add this code somewhere inside <body></body> section where you want to show the widget)')

    const instructionsList = [
        firstStepInstruction,
        scriptEmbed,
        secondStepInstruction,
        divEmbed
    ]

    const handleCopyInstructions = async () => {
        try {
            await navigator.clipboard.writeText(instructionsList.join('\n\n'))
            enqueueSnackbar(t('Copied'), {variant: 'success'})
        } catch (error) {
            console.error('Failed to copy instructions', error)
            enqueueSnackbar(t('Something went wrong'), {variant: 'error'})
        }
    }

    return (
        <Stack sx={{flexDirection: 'column', mt: '32px'}}>

            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
            >

                <Typography variant={'h6'}>
                    {t('How to use this widget on your website')}
                </Typography>

                <Button
                    variant="outlined"
                    endIcon={<ContentCopyRoundedIcon/>}
                    onClick={handleCopyInstructions}
                    sx={{flexShrink: 0}}
                >
                    {t('Copy instructions')}
                </Button>

            </Stack>

            <Alert
                severity={"info"}
                variant={'outlined'}
                style={{marginTop: '32px'}}
            >
                {firstStepInstruction}
            </Alert>

            <Typography
                style={{
                    fontFamily: 'monospace',
                    backgroundColor: CommonThemeColors.sys.primary.light,
                    padding: 20,
                    marginTop: '20px'
                }}
            >
                {scriptEmbed}
            </Typography>

            <Alert
                severity={"info"}
                variant={'outlined'}
                style={{marginTop: '32px'}}
            >
                {secondStepInstruction}
            </Alert>

            <Typography
                style={{
                    fontFamily: 'monospace',
                    backgroundColor: CommonThemeColors.sys.primary.light,
                    padding: 20,
                    marginTop: '20px'
                }}
            >
                {divEmbed}
            </Typography>

            <Card
                variant="outlined"
                sx={{mt: 4, borderColor: "divider", boxShadow: "none", borderRadius: 2}}
            >
                <CardContent sx={{p: 0, "&:last-child": {pb: 0}}}>
                    <Stack
                        direction={{xs: "column", sm: "row"}}
                        alignItems={{xs: "stretch", sm: "center"}}
                        justifyContent="space-between"
                        gap={2}
                        sx={{p: 2, minHeight: 88, boxSizing: "border-box"}}
                    >
                        <Stack direction="row" alignItems="center" gap={2} sx={{minWidth: 0}}>
                            <Box
                                aria-hidden="true"
                                sx={{
                                    width: 40,
                                    height: 40,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    color: "primary.main",
                                }}
                            >
                                <SchoolOutlinedIcon/>
                            </Box>

                            <Stack sx={{minWidth: 0}}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t("Booking Engine integration guide")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("Follow the step-by-step instructions for websites and hotel apps.")}
                                </Typography>
                            </Stack>
                        </Stack>

                        <Button
                            component="a"
                            href={INTEGRATION_GUIDE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            endIcon={<OpenInNewRoundedIcon/>}
                            sx={{alignSelf: {xs: "flex-start", sm: "center"}, flexShrink: 0}}
                        >
                            {t("Learn more")}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

        </Stack>
    )

}

export default BookingEngineInstructions
